import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../api/axiosConfig';
import AuthContext from '../contexts/AuthContext';
import logo from '../logo.jpg';

const MIN_PASSWORD = 8;

/**
 * 비밀번호 재설정 링크를 연 화면.
 *
 * 로그인하지 못하는 사람이 여는 곳이라 로그인 밖에 있다. 초대 화면과 나란히
 * 두었지만 하는 일이 다르다 -- 저쪽은 계정을 만들고 이쪽은 이미 있는 계정을
 * 연다. 그래서 이름과 이메일은 이미 정해져 있고 물어보는 것은 새 비밀번호뿐이다.
 */
const PasswordResetPage = () => {
    const { token } = useParams();
    const navigate = useNavigate();
    const { login } = useContext(AuthContext);

    const [target, setTarget] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [password, setPassword] = useState('');
    const [again, setAgain] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        let alive = true;
        api
            .get(`/password-resets/token/${token}`)
            .then((res) => alive && setTarget(res.data))
            .catch(() => {
                if (!alive) return;
                // 서버는 만료인지 이미 썼는지 말하지 않는다. 화면도 그대로 둔다.
                setError('쓸 수 없는 링크입니다. 관리자에게 다시 요청해 주세요.');
            })
            .finally(() => alive && setLoading(false));
        return () => {
            alive = false;
        };
    }, [token]);

    const submit = async (e) => {
        e.preventDefault();
        if (password.length < MIN_PASSWORD) {
            setError(`비밀번호는 ${MIN_PASSWORD}자 이상으로 정해 주세요.`);
            return;
        }
        if (password !== again) {
            setError('두 번 적은 비밀번호가 다릅니다.');
            return;
        }
        setError('');
        setSaving(true);
        try {
            await api.post(`/password-resets/token/${token}/complete`, { password });
            // 방금 정한 것으로 바로 들어간다. 다시 적게 할 이유가 없다.
            const { data } = await api.post('/auth/login', {
                email: target.email,
                password,
            });
            const me = await login(data.access_token, data.refresh_token);
            navigate(me?.role === 'STAFF' ? '/today' : '/', { replace: true });
        } catch (err) {
            setError(err.response?.data?.message || '바꾸지 못했습니다.');
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="d-flex align-items-center justify-content-center min-vh-100">
                <div className="spinner-border text-primary" role="status"></div>
            </div>
        );
    }

    return (
        <div className="d-flex align-items-center justify-content-center min-vh-100 bg-light px-3">
            <div className="bg-white rounded-4 border p-4 w-100" style={{ maxWidth: '400px' }}>
                <div className="text-center mb-4">
                    <img src={logo} alt="바로보고" height="44" className="rounded mb-3" />
                    {target ? (
                        <>
                            <h5 className="fw-bold mb-1">새 비밀번호를 정해 주세요</h5>
                            <p className="text-muted small mb-0">
                                {target.name}님
                                {target.companyName && ` · ${target.companyName}`}
                            </p>
                        </>
                    ) : (
                        <h5 className="fw-bold mb-1">링크를 열 수 없습니다</h5>
                    )}
                </div>

                {error && <div className="alert alert-warning py-2 small">{error}</div>}

                {target && (
                    <form onSubmit={submit}>
                        <div className="mb-3">
                            <label className="form-label fw-bold small">이메일</label>
                            {/* 바꿀 수 없다. 이 주소로 로그인한다. */}
                            <input className="form-control bg-light" value={target.email} disabled />
                        </div>
                        <div className="mb-3">
                            <label className="form-label fw-bold small">새 비밀번호</label>
                            <input
                                type="password"
                                className="form-control"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder={`${MIN_PASSWORD}자 이상`}
                                autoFocus
                            />
                        </div>
                        <div className="mb-4">
                            <label className="form-label fw-bold small">새 비밀번호 확인</label>
                            <input
                                type="password"
                                className="form-control"
                                value={again}
                                onChange={(e) => setAgain(e.target.value)}
                            />
                        </div>
                        <button
                            type="submit"
                            className="btn btn-primary w-100 fw-bold"
                            disabled={saving}
                        >
                            {saving ? '바꾸는 중…' : '바꾸고 로그인'}
                        </button>
                        {/* 다른 기기에서 로그인해 둔 것이 끊긴다는 사실은 미리
                            알려 준다. 앱이 갑자기 로그아웃되면 고장으로 읽힌다. */}
                        <div className="text-muted mt-3" style={{ fontSize: '0.75rem' }}>
                            바꾸면 다른 기기에서 로그인해 둔 것은 모두 끊깁니다.
                        </div>
                    </form>
                )}

                {!target && (
                    <button
                        className="btn btn-white border w-100"
                        onClick={() => navigate('/login')}
                    >
                        로그인으로
                    </button>
                )}
            </div>
        </div>
    );
};

export default PasswordResetPage;
